/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import * as React from 'react';
import ouiStyle from '@goldwasserexchange/oui-style';
import { useHookstate } from '@hookstate/core';
import { Untracked } from '@hookstate/untracked';
import {
  newsLetterState, newsLetterSubmitErrorState, newsLetterSubmittingState, newsLetterTouchedState, newsLetterValidationState, validateNewsLetterSyncAt,
} from '../../../../state';

const style: ouiStyle.InStyleWithMediaQueries = {
  backgroundColor: ouiStyle.Constants.Colors.inverted,
  paddingVertical: 'simple',
  paddingHorizontal: 'double',
  borderRadius: 'full',
  widthLines: 'full',
  borderWidth: 'simple',
  borderStyle: 'solid',
  borderColor: ouiStyle.Constants.Colors.inverted,
};

export const NewsLetterBoxInputContent = (props: {
  name: string,
  type: React.InputHTMLAttributes<HTMLInputElement>['type'],
  placeholder: string,
  backgroundColor?: ouiStyle.InStyleWithMediaQueries['backgroundColor'],
}) => {
  const {
    name,
    type,
    placeholder,
    backgroundColor = style.backgroundColor,
  } = props;
  const state = useHookstate(newsLetterState[name]);
  const valid = useHookstate(newsLetterValidationState[name]);
  const touched = useHookstate(newsLetterTouchedState[name]);
  const submitting = useHookstate(newsLetterSubmittingState);
  const validateTimeout = React.useRef<NodeJS.Timeout | null | undefined>(null);
  React.useEffect(() => {
    if (validateTimeout.current != null) {
      clearTimeout(validateTimeout.current);
    }
    validateTimeout.current = setTimeout(() => {
      validateNewsLetterSyncAt(name);
    }, 500);
    return () => {
      if (validateTimeout.current != null) {
        clearTimeout(validateTimeout.current);
      }
    };
  }, [state.get(), validateTimeout]);
  React.useEffect(() => {
    if (submitting.get() === true && validateTimeout.current != null) {
      clearTimeout(validateTimeout.current);
    }
  }, [submitting.get(), validateTimeout]);
  const isValid = touched.get() === false ? true : valid.get();
  const css = React.useMemo(() => ouiStyle.mediaQuery({
    ...ouiStyle.makeCss({
      ...style,
      backgroundColor,
      borderColor: isValid ? ouiStyle.Constants.Colors.inverted : ouiStyle.Constants.Colors.danger,
    }),
    '::placeholder': {
      color: ouiStyle.Constants.Colors.accent,
      opacity: 1,
      fontWeight: ouiStyle.Constants.FontWeightProp.intermediary,
    },
  }), [isValid, backgroundColor]);
  const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (Untracked(newsLetterSubmitErrorState).get() != null) {
      newsLetterSubmitErrorState.set(null);
    }
    state.set(event.target.value);
  }, [state]);
  const onBlur = React.useCallback(() => {
    validateNewsLetterSyncAt(name);
    touched.set(true);
  }, [name, touched]);
  return (
    <input
      id={name}
      name={name}
      type={type}
      css={css}
      placeholder={placeholder}
      value={state.get()}
      onChange={onChange}
      onBlur={onBlur}
      disabled={submitting.get()}
    />
  );
};
